import { toast } from "sonner";

import { downloadOrganizationSessionsReport, fetchOrganizationSessionsReport, type OrgSessionsReportFilters } from "@/lib/api";
import { HttpError } from "@/lib/http";
import { SWITCH_TARGET_EXPONENTS } from "@/lib/operatingCurrencySwitch";
import type { OrgReportTotalsMoney } from "@/lib/orgReportTotals";
import type { Translate } from "@/app/localization";
import { errorCode, errorMessage } from "@/app/serverErrors";

// The admin CSV export of an org's charging sessions, shared by the report view and the
// organization panel: the server's money contract 2 CSV, saved exactly as served. The
// browser never builds, converts or relabels the file.

/** The server refused the CSV because its selection spans more than one currency
 *  (409 money_contract_incompatible, reason mixed_currency_set): one file holds one currency. */
export function isMixedCurrencyCsvRefusal(error: unknown): boolean {
  if (!(error instanceof HttpError) || error.status !== 409) return false;
  if (errorCode(error) !== "money_contract_incompatible") return false;
  const contract = (error.body as { money_contract?: unknown }).money_contract;
  return typeof contract === "object" && contract !== null
    && (contract as { reason?: unknown }).reason === "mixed_currency_set";
}

/** Why the CSV did not download. The server's own message is never shown. */
export function csvExportErrorMessage(t: Translate, error: unknown): string {
  return errorMessage(t, error, { fallback: "operator.orgs.report.generate_failed" });
}

/** The currencies a mixed selection can be exported in: the served entries of its own
 *  totals, in served order. Without readable entries, every assignable operating currency;
 *  the server stays authoritative for the chosen code either way. */
export function csvCurrencyChoices(money: OrgReportTotalsMoney | undefined): readonly string[] {
  const served = money?.status === "value" ? money.currencies.map((c) => c.summary.currency) : [];
  return served.length > 0 ? [...new Set(served)] : Object.keys(SWITCH_TARGET_EXPONENTS);
}

/** The choices of a caller with no loaded report: one read of the same selection's JSON report. */
export async function fetchCsvCurrencyChoices(orgId: number, filters: OrgSessionsReportFilters): Promise<readonly string[]> {
  try {
    const read = await fetchOrganizationSessionsReport(orgId, filters);
    return csvCurrencyChoices(read.status === "value" ? read.value.totals.money : undefined);
  } catch {
    return csvCurrencyChoices(undefined);
  }
}

/** Hand a downloaded file to the browser under its own name. */
export function saveFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export type CsvExportOutcome = "saved" | "mixed_currency" | "failed";

/** Download the server CSV for `filters` and save it. An unfiltered mixed-currency refusal
 *  is left to the caller, who offers the choice; every other outcome is a toast here. With
 *  `csvCurrency` the request is already a choice, so any refusal is a plain failure. */
export async function exportOrgSessionsCsv(
  t: Translate,
  orgId: number,
  filters: OrgSessionsReportFilters,
  csvCurrency?: string,
): Promise<CsvExportOutcome> {
  try {
    const { blob, filename } = await downloadOrganizationSessionsReport(orgId, filters, csvCurrency);
    saveFile(blob, filename);
    toast.success(t("operator.orgs.report.downloaded"));
    return "saved";
  } catch (e) {
    if (!csvCurrency && isMixedCurrencyCsvRefusal(e)) return "mixed_currency";
    toast.error(csvExportErrorMessage(t, e));
    return "failed";
  }
}
